// Astrea Ops Platform - AI Form Assistant conversation thread data-access layer
// (ai_form_conversations)
//
// 21 September 2026. One row per project, same shape as event_forms/debriefs in
// eventForm.js, but loaded differently: event_forms is bulk-loaded across every
// project up front because cards/overview/calculations need it broadly. This
// conversation thread has no app-wide need - only EventFormTab does - so it's loaded
// lazily, once, when that tab mounts for a given project, rather than folded into the
// central loadEventFormsByProject() sweep.
//
// `messages` is stored and returned exactly as api/ai-form-assist.js hands it back:
// an array of {role, content} objects in Anthropic Messages API shape, content always
// an array of {type:"text", text} blocks (never raw document bytes or tool-use/
// tool-result blocks - see that file's header for why). This module doesn't interpret
// the conversation at all, just persists whatever EventFormTab's chat state gives it.

import { supabase } from './supabaseClient';

// Keeps the persisted thread bounded - a long-running conversation shouldn't grow the
// row (or the body of the next request that replays it) without limit. 40 messages is
// generous headroom for how this tab actually gets used (a handful of drafting rounds
// per event) while staying well under Vercel's 4.5MB function payload ceiling even
// with a few sizeable pasted-transcript turns in the mix.
const MAX_STORED_MESSAGES = 40;

// Loads the conversation thread for one project. Returns [] if the project has no
// thread yet (never generated a draft, or a project from before this slice shipped).
export async function loadAiConversation(projectId) {
  if (!projectId) return [];
  const { data, error } = await supabase
    .from('ai_form_conversations')
    .select('messages')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) throw error;
  return (data && Array.isArray(data.messages)) ? data.messages : [];
}

// Upserts the full thread for one project. Called after every AI turn completes
// (EventFormTab owns the in-memory message list; this just persists it), trimming to
// the most recent MAX_STORED_MESSAGES so the thread can't grow the row unbounded.
export async function saveAiConversation(projectId, messages) {
  if (!projectId) return;
  const trimmed = (Array.isArray(messages) ? messages : []).slice(-MAX_STORED_MESSAGES);
  const { error } = await supabase
    .from('ai_form_conversations')
    .upsert(
      { project_id: projectId, messages: trimmed, updated_at: new Date().toISOString() },
      { onConflict: 'project_id' }
    );
  if (error) throw error;
}
